import { Link } from 'react-router-dom';
import { api, type CardView, type CrossFitPayload } from '../api';
import { isCrossFit, allEntries } from '../briefing';
import { CaptureLine, CrossFitView } from '../components/crossfit';
import { useOpenDetail } from '../components/detail-link';
import { EmptyState, ErrorState, Loading, PageIntro, useAsync } from '../components/ui';
import { formatLocalDay, localToday } from '../format';

/**
 * The training page shows the newest reading of the day. It starts from the
 * CrossFit card of the current edition; when a newer version of that same day's
 * card was stored after the edition was prepared, it shows the newer one and
 * says so. When the edition has none, it falls back to the newest stored
 * CrossFit card and says which date that is, rather than presenting an old day
 * as today.
 */
async function loadTraining(): Promise<{ view: CardView | null; fromEdition: boolean; newerThanEdition: boolean }> {
  const briefing = await api.currentBriefing();
  const fromEdition = briefing.briefing ? allEntries(briefing).find(isCrossFit) ?? null : null;
  const latest = (await api.search({ section: 'crossfit', sort: 'newest', limit: 1 }).catch(() => null))?.results[0] ?? null;
  if (fromEdition) {
    const newer =
      latest !== null &&
      latest.card.id === fromEdition.card.id &&
      latest.version.id !== fromEdition.version.id &&
      latest.version.version > fromEdition.version.version;
    return newer ? { view: latest, fromEdition: true, newerThanEdition: true } : { view: fromEdition, fromEdition: true, newerThanEdition: false };
  }
  return { view: latest, fromEdition: false, newerThanEdition: false };
}

export function CrossFitPage() {
  const { data, error, loading, reload } = useAsync(loadTraining, []);
  const open = useOpenDetail();

  if (loading && !data) return <Loading label="Trainingsdaten werden geladen…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data?.view) {
    return (
      <>
        <PageIntro eyebrow="CrossFit" title="Workout. Termine. Teilnehmende." />
        <EmptyState title="Noch keine Trainingsdaten.">
          <p className="muted">Es wurde noch keine CrossFit-Lesung gespeichert. Ob die Lesung ausfiel, steht im <Link to="/datenstatus">Datenstatus</Link>.</p>
        </EmptyState>
      </>
    );
  }
  const p = data.view.version.payload as CrossFitPayload;
  const isToday = p.local_date === localToday();

  return (
    <>
      <PageIntro eyebrow={`CrossFit · ${formatLocalDay(p.local_date)}`} title="Workout. Termine. Teilnehmende.">
        Alle Trainingsinformationen an einem Ort, jeweils mit ihrem Erfassungsstand.
      </PageIntro>
      {!isToday && (
        <div className="notice notice-warn">
          <strong>Nicht von heute</strong>
          Diese Lesung beschreibt {formatLocalDay(p.local_date)}.
          {data.fromEdition ? ' Sie stammt aus der aktuellen Ausgabe.' : ' Die aktuelle Ausgabe enthält keine CrossFit-Karte; gezeigt wird die neueste gespeicherte.'}
        </div>
      )}
      {data.newerThanEdition && (
        <div className="notice notice-info" role="note">
          <strong>Neuere Lesung als in der Ausgabe</strong>
          Diese Lesung kam nach dem Stand der aktuellen Ausgabe; die nächste Ausgabe übernimmt sie.
        </div>
      )}
      <CaptureLine p={p} />
      <CrossFitView p={p} />
      <p className="board-foot">
        <button type="button" className="text-button" onClick={(e) => open(data.view!.version.id, e.currentTarget)}>
          Version &amp; Belege dieser Lesung
        </button> · <Link to="/crossfit/verlauf">Verlauf: Workouts und Teilnahmen</Link>
      </p>
    </>
  );
}
